import React from 'react';

const styles = {
  container: {
    maxWidth: '900px',
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: '2rem',
    fontFamily: 'Georgia, serif',
    lineHeight: '1.8',
    color: '#333',
  },
  header: {
    textAlign: 'center' as const,
    marginBottom: '3rem',
    borderBottom: '2px solid #ddd',
    paddingBottom: '2rem',
  },
  title: {
    fontSize: '2.5rem',
    marginBottom: '0.5rem',
    color: '#2c3e50',
  },
  subtitle: {
    fontSize: '1rem',
    fontStyle: 'italic',
    color: '#666',
    marginBottom: '0.25rem',
  },
  sectionTitle: {
    fontSize: '1.8rem',
    marginTop: '2.5rem',
    marginBottom: '1.5rem',
    color: '#2c3e50',
    borderLeft: '4px solid #8b7355',
    paddingLeft: '1rem',
  },
  epigraph: {
    fontSize: '1.1rem',
    fontStyle: 'italic',
    marginBottom: '1rem',
    paddingLeft: '2rem',
    borderLeft: '3px solid #ddd',
    color: '#555',
  },
  verse: {
    fontSize: '0.95rem',
    marginBottom: '1.5rem',
    padding: '1rem',
    backgroundColor: '#f9f7f4',
    borderRadius: '4px',
  },
  preface: {
    backgroundColor: '#faf9f7',
    padding: '1.5rem',
    borderRadius: '4px',
    marginBottom: '2rem',
    borderLeft: '4px solid #8b7355',
  },
  subsection: {
    marginBottom: '1.5rem',
  },
  subsectionTitle: {
    fontSize: '1.2rem',
    fontWeight: 'bold' as const,
    marginTop: '1.5rem',
    marginBottom: '1rem',
    color: '#2c3e50',
  },
  quote: {
    fontStyle: 'italic',
    paddingLeft: '2rem',
    marginBottom: '1rem',
    borderLeft: '3px solid #ddd',
    color: '#555',
  },
  reference: {
    fontSize: '0.9rem',
    fontStyle: 'italic',
    color: '#888',
    marginBottom: '1rem',
  },
} as const;

export default function PrayerGuideWrittenWork(this: any) {
  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Sermons on the Lord's Prayer</h1>
        <p style={styles.subtitle}>A Guide to Effective Praying</p>
        <p style={styles.subtitle}>By DAROLD H. MORGAN</p>
        <p style={styles.subtitle}>FIRST BAPTIST CHURCH, Sherman, Texas</p>
      </header>

      <section style={styles.preface}>
        <h2 style={{ fontSize: '1.4rem', marginBottom: '1rem' }}>PREFACE</h2>
        <p>
          Under the title of "A Guide to Effective Praying," this little volume contains a series of sermons on The Lord's Prayer, which I preached in July and August, 1959, to congregations in the First Baptist Church of Sherman, Texas. I have been highly complimented by the response to these sermons and have had them mimeographed because of the repeated requests for them.
        </p>
        <p>
          Additional material that will prove of much value to the serious student of the Lord's Prayer -- and source material which has assisted greatly in the preparation of these sermons -- are these: George Buttrick, <em>So We Believe - So We Pray</em>; Alan Redpath, <em>Victorious Praying</em>; Clarence Kerr, <em>Pattern for Powerful Praying</em>; and Charles Allen, <em>God's Psychiatry</em>.
        </p>
        <p>
          I am more convinced than ever that Christians need to understand Jesus' approach to prayer, and out of this understanding can come the desperately needed power for implementing the Christian's task and commission for today.
        </p>
        <p>
          A special word of thanks goes to my secretary, Miss Hazel Cole, for performing the laborious task of transcribing these messages and then typing the stencils, and to Mrs. Laura Young for a generous amount of assistance.
        </p>
        <p style={{ marginTop: '1.5rem', fontWeight: 'bold' }}>Darold H. Morgan</p>
      </section>

      <section>
        <h2 style={styles.sectionTitle}>Our Father Which Art in Heaven</h2>
        <div style={styles.verse}>
          <p style={styles.epigraph}>"After this manner therefore, pray ye: Our Father which art in Heaven, Hallowed by Thy Name." (Matthew 6:9)</p>
        </div>

        <p>
          A recent survey was taken in a number of churches based on the simple question, "What sermon do you want most to hear?" The reply that was heard most often was: "How can I make prayer more effective?"
        </p>

        <p>
          The reason for this can be found easily. We hear of so many life-changing experiences related to prayer, and we want to possess these for ourselves. Most of us know some sense of frustration in our prayer life because we feel that there is so much more for us in that realm than we have been getting.
        </p>

        <p>
          Perhaps the most effective source of information and scriptural understanding is immediately apparent in a study of the Lord's prayer. So here are eight messages on prayer, all basically built around the theme, "A Guide to Effective Praying - A Study of the Model Prayer."
        </p>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>I. The Disciples' Request</h3>
          <p>
            Before we come to these ideas, however, it is absolutely essential that we place ourselves in the position of the disciples. According to Luke's version, all of this came about because they came to Jesus one day and asked him earnestly, "Lord, teach us to pray."
          </p>
          <p>
            Here were men who had prayed all of their lives, but here were men who saw that prayer could mean much more to them. The disciples sensed that Jesus alone held the key to effective praying. Early in the morning and again late at night they knew he had something they did not have because he was alone with God.
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>II. Submission to Divine Sovereignty</h3>
          <p>
            The word "father" involves submission to divine sovereignty. There can be no effective praying without this. Just like a child loves and trusts his earthly father, so are we challenged by the teachings and example of Jesus to love and trust our Heavenly Father.
          </p>
          <p>
            So when Jesus uses this first phrase, "Our Father which art in Heaven," He strikes the deepest chord that can be heard in Christendom. It calls for the sovereignty of God in the human heart.
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>III. Spiritual Adoption</h3>
          <p>
            Through much of the New Testament there is a great analogy of spiritual adoption. Paul draws on this analogy in Galatians because through the Roman world it was a familiar experience. "But when the fullness of time was come, God sent forth His Son, made of a woman, made under the law. To redeem them that were under the law, that we might receive the adoption of sons… wherefore thou art no more a servant, but a son; and if a son, then an heir of God through Christ." (Gal. 4:4-5,7)
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>IV. Communion with God</h3>
          <p>
            The insistent suggestion that before prayer is ever prayer, there must be the establishment of communion with the father. Prayer is essentially communion with God. Prayer is first and last a blessed experience of real and personal fellowship with the God the Father of our Lord, Jesus Christ.
          </p>
        </div>
      </section>

      <section>
        <h2 style={styles.sectionTitle}>Hallowed Be Thy Name</h2>
        <div style={styles.verse}>
          <p style={styles.epigraph}>"After this manner therefore pray ye: Our Father which art in Heaven, Hallowed be Thy Name." (Matthew 6:9)</p>
        </div>

        <p>
          One of the first surprises in a study of this part of the Lord's Prayer is to understand that this wording - "Hallowed by thy Name" — is actually a request rather than an addressing of God.
        </p>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>I. A Godless World</h3>
          <p>
            "Father - this is a pagan, godless world that does not revere The Name." The very fact that we are taught to pray this way tells us that the name of God is hallowed or made known as holy. The opposite to hallow is to profane.
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>II. Overcoming Irreverence</h3>
          <p>
            "Father - Help us to Overcome the Irreverence of our Day." We have irreverence in the speech that we hear today. Is not one of the most useless things that many men and women and young people do today - the use of profanity - the taking of the name of God in vain?
          </p>
          <p>
            How we need a guard on our lips that our speech may be hallowed. From Exodus 20:7: "Thou shalt not take the name of the Lord thy God in vain; for the Lord will not hold him guiltless that taketh His name in vain."
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>III. Personal Identification</h3>
          <p>
            "Hallowed by thy name" — to pray this means a personal identification with the overcoming of irreverence of the day. To pray that prayer means it is our hearts' desire in bearing the name of Christ that others may see His character expressed in our lives.
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>IV. Daily Transformation</h3>
          <p>
            Here is a petition which can alter every hour of every day. It can be done for God's power can bring this change in us. The Bible plainly teaches this: "I can do all things through Christ which strengtheneth me." (Phil. 4:13)
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>V. Hastening the Consummation</h3>
          <p>
            "Father -Hasten the Day when all the World shall Honor thy Name." The following Scriptures point to this hastening consummation:
          </p>
          <div style={styles.quote}>
            <p>
              "Wherefore God also hath highly exalted him, and given him a name which is above every name: That at the name of Jesus every knee should bow, of things in heaven and things in earth, and things under the earth: And that every tongue should confess that Jesus Christ is Lord, to the glory of God the Father." (Phil. 2:9-11)
            </p>
          </div>
        </div>
      </section>

      <section>
        <h2 style={styles.sectionTitle}>Thy Kingdom Come</h2>
        <div style={styles.verse}>
          <p style={styles.epigraph}>"Thy kingdom come, Thy will be done in earth, as it is in Heaven." (Matthew 6:10)</p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>The Sovereign Kingdom</h3>
          <p>
            Now there is far more involved in these words than we see at first glance — 'Thy Kingdom Come." Here is the second request in the Lord's Prayer that contains some very innovating ideas that ought to be applied to our prayer life, in our church life, and in every side of life.
          </p>
          <p>
            It is not a democratic relationship between God and man. God is the Creator, the sustainer, and the sovereign ruler of the universe. We are his subjects; we are his vassals. We are to do his will, and one of the most desperately needed lessons in the field of religion is this simple idea of truly understanding the reign of God in the human heart.
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>Missionary Expansion</h3>
          <p>
            When we offer this petition, we are praying also that God's reign may be established around the world. The average member of an average Baptist church is doing relatively little praying for the missionary expansion of Christianity.
          </p>
        </div>
      </section>

      <section>
        <h2 style={styles.sectionTitle}>Thy Will Be Done</h2>
        <div style={styles.verse}>
          <p style={styles.epigraph}>"Thy kingdom come. Thy will be done in earth, as it is in Heaven." (Matthew 6:10)</p>
        </div>

        <p>
          "To find God's will is life's greatest discovery. To do God's will is life's greatest achievement." — George W. Truett
        </p>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>God's Will Is the Best</h3>
          <p>
            The first thing that I would say about it is simply that we need to believe that God's will is the best thing in the world for my life. We need to understand that doing God's will is a way of life - a philosophy of life.
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>God's Will Is Understandable</h3>
          <p>
            God's will is understandable - it's discernible - it's knowable! Whenever you come to a time when a decision needs to be made, if the end result is an act that will honor God, you will know what needs to be done.
          </p>
          <p>
            There are a number of very practical ways that will help anybody in trying to find out what God wants them to do: reading and knowing the Bible, counseling with trusted Christian friends, having an open mind when asking God to lead you, and understanding how God has led others in similar choices.
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>God's Will Can Be Done</h3>
          <p>
            God never asks us to do anything that we cannot do. He may ask the difficult in our lives, but He will never ask the impossible. "There hath no temptation taken you such as is common to man, but that God is faithful... with temptation, also make a way of escape that ye may be able to bear it." (1 Cor. 10:13)
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>God's Will Shall Be Done</h3>
          <p>
            Do you believe that God's will ultimately shall be done? There is the will of purpose - God has a purpose in this world, and ultimately his will shall come to pass. There is the will of desire - God wishes that none should perish. And there is the will of command - God commands us to do things.
          </p>
        </div>
      </section>

      <section>
        <h2 style={styles.sectionTitle}>Give Us This Day Our Daily Bread</h2>
        <div style={styles.verse}>
          <p style={styles.epigraph}>"Give us this day our daily bread." (Matthew 6:11)</p>
        </div>

        <p>
          The most anti-climactic part of the entire model prayer comes when the emphasis leaves the series of requests about God, His nature, His Will, and His Kingdom, and turns with abrupt suddenness to the intensely materialistic suggestion, "Give us our daily bread."
        </p>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>Material Blessings Are Proper</h3>
          <p>
            The first thing we glean from our studies here is that it is proper to ask God for material blessings. Jesus knew much about making ends meet in a home. He was the bread-winner for a humble little home in Jerusalem.
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>No Request for Luxury</h3>
          <p>
            It is improper to ask for luxury and extravagance. God's measure of giving for these material needs is seen in the word "bread." Bread refers to the basic needs of life. God gives us grace to be extraordinary in the ordinary circumstances of today.
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>Nothing Is Too Small</h3>
          <p>
            Nothing is too small in life to bring to God. Many have been spiritually handicapped because they have felt themselves entirely too insignificant in God's sight. Go to the analogy of the earthly father and the earthly child. Every aspect of that child's life is of vital importance to the father.
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>Concern for Others</h3>
          <p>
            The request is not —"give me my bread," but "give us our daily bread." The Christian must be concerned about the physical and material welfare of other Christians. We must be deeply concerned about the welfare of others.
          </p>
        </div>
      </section>

      <section>
        <h2 style={styles.sectionTitle}>Forgive Us Our Debts</h2>
        <div style={styles.verse}>
          <p style={styles.epigraph}>"And forgive us our debts, as we forgive our debtors." (Matthew 6:12)</p>
        </div>

        <p>
          This is the only phrase in the model prayer that Jesus saw fit to amplify. Matthew 6:14, the verse that follows the Lord's Prayer, gives us this exceptionally strong teaching, "for if ye forgive men their trespasses, your Heavenly Father will also forgive yours."
        </p>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>Continuous Forgiveness</h3>
          <p>
            Jesus is saying in this prayer request that we need forgiveness as often as we need daily bread. One of the major ideas contained in this petition is an awareness of our need for daily purification and a daily dedication to the Will of the Heavenly Father.
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>Consciousness of Sin</h3>
          <p>
            We need to have a continual consciousness of sin - not a perpetual guilt complex, but to feel no sense of obligation to God is certainly worse than the worst features of a guilt complex. We need daily to face the fact of spiritual honesty.
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>Forgiveness Is Conditional</h3>
          <p>
            Forgiveness is conditional upon our forgiving others. "The words 'forgiving and forgiven' are inseparable twins." We cannot have effective prayer if we harbour ill-will and an unforgiving spirit toward someone else.
          </p>
        </div>
      </section>

      <section>
        <h2 style={styles.sectionTitle}>Lead Us Not Into Temptation</h2>
        <div style={styles.verse}>
          <p style={styles.epigraph}>"And lead us not into temptation, but deliver us from evil: For thine is the kingdom, and the power, and the glory, for ever. Amen." (Matthew 6:13)</p>
        </div>

        <p>
          In the model prayer there are three prayers to pray for ourselves. One is for the present: "Give us this day our daily bread." One is for the past and the present: "Forgive us our debts as we forgive our debtors;" and one is for the future: "Lead us not into temptation."
        </p>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>God Does Not Tempt to Evil</h3>
          <p>
            "Let no man say when he is tempted, I am tempted of God, for God cannot be tempted with evil, neither tempteth He any man." (James 1:12-13) If these words are translated back into the original Aramaic language Jesus spoke, the phrase would read: "And let us not yield to temptation."
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>Awareness of Temptation</h3>
          <p>
            One of the most important things about living a strong spiritual life is to be aware of our problems and our enemies. We are not free from the power of temptation. The Devil, like a roaring lion, walks about in our world, seeking whomsoever he may devour.
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>Power to Overcome</h3>
          <p>
            Buried in this prayer request is the idea that you can defeat him, that you can have strength enough to live the right kind of life regardless of the power of the tempter.
          </p>
          <p>
            "There hath no temptation taken you such as is common to man, but that God is faithful in that He will not suffer you to be tempted above that which ye are able, but will with temptation make a way of escape, and you will be able to bear it." (1 Cor. 10:13)
          </p>
        </div>
      </section>

      <section>
        <h2 style={styles.sectionTitle}>For Thine Is the Kingdom</h2>
        <div style={styles.verse}>
          <p style={styles.epigraph}>"For thine is the kingdom, and the power, and the glory, for ever. Amen." (Matthew 6:13)</p>
        </div>

        <p>
          Any student of the New Testament can tell us that these final and beautiful words of the Lord's Prayer are not part of the original prayer as we have it stated in the oldest manuscripts. However, no true scholar of the New Testament, nor any genuine Christian would deny that these words embrace a spirit and a finality which is in beautiful accord with all that has been said in the model prayer.
        </p>

        <p>
          It is not far from the truth to say that every prayer should begin and end with praise and thanksgiving.
        </p>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>God's Eternal Kingdom</h3>
          <p>
            One is certainly the fact that we believe in God's Eternal Kingdom. Jesus taught that the Kingdom of God is the only Eternal Kingdom. From the vantage point of time, no one could possibly think of the political and military future of the centuries stretching out ahead unless Rome was a part of the picture. Yet Jesus taught a kingdom that would stand forever.
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>God's Infinite Power</h3>
          <p>
            Secondly, we believe in God's infinite power. Notice the present tense, "Thine is the power." Yet what an enigma we have on our hands. We believe in God's power as it is stated in a setting like this, but how seldom do we accept what it can mean to our own personal lives.
          </p>
        </div>

        <div style={styles.subsection}>
          <h3 style={styles.subsectionTitle}>God's Everlasting Glory</h3>
          <p>
            Thirdly we believe in God's everlasting glory. Moses said to God, "I pray thee show me thy glory," and God said, "I will make all my goodness pass before you." God's glory is in His goodness, His mercy, His love, His grace, His patience, His promises.
          </p>
          <p>
            Jesus indicated that our needs are few — bread, forgiveness, guidance. We would not do damage to the model prayer by saying that ours is a fourth need, the sacred need to praise Almighty God because of His Eternal Kingdom, because of His infinite power, and because of His everlasting glory.
          </p>
        </div>
      </section>
    </article>
  );
}